import React from 'react';
import { Row, Col } from 'react-bootstrap';
import {
  type SubscriptionVariant,
  type SubscriptionData,
} from '../../lib/types';
import { SubscriptionCard } from '../SubscriptionCard';
import { useAppContext } from '../../context/shared';
import styles from '../../styles/SubscriptionsList.module.css';
import CarouselOnMobile from '../CarouselOnMobile';
import { RichText } from '../RichText';

const ALLOWED_VARIANTS: string[] = ['monthly', 'biannualMonthly', 'annualMonthly'];

type SubscriptionsListProps = {
  readonly subscriptions: SubscriptionData[];
  readonly isCartReady?: boolean;
  readonly isSmall?: boolean;
  readonly selectedSubscription?: SubscriptionVariant;
  readonly onSubscribeClick?: (subscription: SubscriptionVariant) => void;
};

export function SubscriptionsList(props: SubscriptionsListProps) {
  const {
    subscriptions,
    isCartReady,
    isSmall,
    selectedSubscription,
    onSubscribeClick,
  } = props;

  const { localization, isMobile } = useAppContext();
  const [filteredSubscriptions, setFilteredSubscriptions] = React.useState<
    Array<SubscriptionData>
  >([]);

  React.useEffect(() => {
    const monthlyOnly = subscriptions.filter((s) =>
      ALLOWED_VARIANTS.includes(s.frequency)
    );

    // If no subscription has bestValue (e.g. it was on a removed prepaid plan),
    // assign it to annualMonthly as fallback
    const hasBestValue = monthlyOnly.some((s) => s.bestValue);
    const withBestValue = hasBestValue
      ? monthlyOnly
      : monthlyOnly.map((s) =>
        s.frequency === 'annualMonthly' ? { ...s, bestValue: true } : s
      );

    if (isMobile) {
      // Best value subscription is always first on mobile
      const bestPriceSubscriptionIndex = withBestValue.findIndex(
        (subscription) => subscription.bestValue
      );
      const reordered = [...withBestValue];
      const [bestPriceSubscription] = reordered.splice(bestPriceSubscriptionIndex, 1);
      reordered.unshift(bestPriceSubscription);
      setFilteredSubscriptions(reordered);
    } else {
      setFilteredSubscriptions(withBestValue);
    }
  }, [subscriptions, isMobile]);

  return (
    <div className={`${isSmall ? 'gy-5' : 'px-xl-5 g-5'} my-5`}>
      <CarouselOnMobile
        desktopChildren={
          <Row
            xs={1}
            lg={3}
            className={`${isSmall ? 'gy-5' : 'px-xl-5 g-5'} my-5 justify-content-around`}
          >
            {filteredSubscriptions.map((subscription: SubscriptionData) => (
              <Col
                key={subscription.frequency}
                className={`${
                  subscription.bestValue
                    ? styles.orderFirstOnMobile
                    : styles.orderLastOnMobile
                } animate__animated animate__fadeIn px-0 ${subscription.bestValue} px-3`}
              >
                <SubscriptionCard
                  isSelected={selectedSubscription === subscription.frequency}
                  isSmall={isSmall}
                  isDisabled={isCartReady}
                  subscription={subscription}
                  onSubscribeClick={onSubscribeClick}
                />
              </Col>
            ))}
          </Row>
        }
        mobileChildren={filteredSubscriptions.map(
          (subscription: SubscriptionData) => (
            <SubscriptionCard
              key={subscription.frequency}
              isSelected={selectedSubscription === subscription.frequency}
              isSmall={isSmall}
              isDisabled={isCartReady}
              subscription={subscription}
              onSubscribeClick={onSubscribeClick}
            />
          )
        )}
      ></CarouselOnMobile>

      <div className={`text-center ${isMobile ? 'pt-5 mt-5' : ''}`}>
        <RichText
          style={{ color: '#4f595f' }}
          content={localization.subscription_infos}
        />
      </div>

    </div>
  );
}
